import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { useLocation } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { RootState } from 'af-reducers';

import * as SettingsUtils from 'af-utils/settings.util';

import BrowserStorageEnum from 'ab-enums/browserStorage.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';

import * as SettingsKeys from 'af-constants/settingsKeys';

import Breadcrumbs from 'af-components/Breadcrumbs';
import TabNavigation from 'af-components/TabNavigation';

import CLIENT from 'af-routes/client';

import ExpenseReportsTable from './ExpenseReportsTable';
import OrderReportsTable from './OrderReportsTable';
import OrdersTable from './OrdersTable';

const BREADCRUMBS = [{ label: 'Orders' }];

const TAB_IDS = {
	ORDERS: 0,
	ORDER_REPORTS: 1,
	EXPENSE_REPORTS: 2,
};

enum TAB_KEYS {
	ORDERS = 'orders',
	ORDER_REPORTS = 'order-reports',
	EXPENSE_REPORTS = 'expense-reports',
}

const TABS = [
	{ id: TAB_IDS.ORDERS, label: 'Orders' },
	{ id: TAB_IDS.ORDER_REPORTS, label: 'Order Reports' },
	{ id: TAB_IDS.EXPENSE_REPORTS, label: 'Expense Reports' },
];

const TAB_MAP = {
	[TAB_IDS.ORDERS]: TAB_KEYS.ORDERS,
	[TAB_IDS.ORDER_REPORTS]: TAB_KEYS.ORDER_REPORTS,
	[TAB_IDS.EXPENSE_REPORTS]: TAB_KEYS.EXPENSE_REPORTS,
} as const;

const ID_MAP = {
	[TAB_KEYS.ORDERS]: TAB_IDS.ORDERS,
	[TAB_KEYS.ORDER_REPORTS]: TAB_IDS.ORDER_REPORTS,
	[TAB_KEYS.EXPENSE_REPORTS]: TAB_IDS.EXPENSE_REPORTS,
} as const;

interface SettingProps {
	tabId: number;
}

type Props = SettingProps & ConnectedProps<typeof connector>;

const OrdersPage: React.FC<Props> = (props) => {

	const {
		companyName,
		hasManageOrdersPermission,
	} = props;

	const { tab } = useParams<{ tab?: keyof typeof ID_MAP; }>();
	const location = useLocation();
	const history = useHistory();

	React.useEffect(() => {
		const defaultTab = TAB_IDS.ORDERS;

		// If the URL doesn't have a tab or it's invalid, replace with a valid default
		if (
			!tab
			|| !(tab in ID_MAP)
		) {
			history.replace(`${CLIENT.COMPANY.ORDER.TABLE(location.state.orgAlias, companyName)}/${TAB_MAP[defaultTab]}`);
		}
	}, [tab, history, location.state.orgAlias, companyName]);

	const activeTabId = React.useMemo(() => tab && tab in ID_MAP ? ID_MAP[tab] : TAB_IDS.ORDERS, [tab]);

	const selectTab = React.useCallback((tabId: number) => {
		if (!hasManageOrdersPermission) {
			history.replace(`${CLIENT.COMPANY.ORDER.TABLE(location.state.orgAlias, companyName)}/${TAB_MAP[TAB_IDS.ORDERS]}`);

			SettingsUtils.setItem(SettingsKeys.ORDERS_TAB(), `${TAB_IDS.ORDERS}`, BrowserStorageEnum.LOCAL_STORAGE);
		} else {
			history.replace(`${CLIENT.COMPANY.ORDER.TABLE(location.state.orgAlias, companyName)}/${TAB_MAP[tabId]}`);

			SettingsUtils.setItem(SettingsKeys.ORDERS_TAB(), `${tabId}`, BrowserStorageEnum.LOCAL_STORAGE);
		}

	}, [companyName, hasManageOrdersPermission, history, location.state.orgAlias]);

	const renderTab = React.useCallback(() => {
		if (!hasManageOrdersPermission) {
			return <OrdersTable />;
		}
		switch (activeTabId) {
			case TAB_IDS.ORDER_REPORTS:
				return <OrderReportsTable />;
			case TAB_IDS.EXPENSE_REPORTS:
				return <ExpenseReportsTable />;
			case TAB_IDS.ORDERS:
			default:
				return <OrdersTable />;
		}
	}, [activeTabId, hasManageOrdersPermission]);

	const tabs = React.useMemo(() => {
		return hasManageOrdersPermission
			? TABS
			: [TABS[0]];
	}, [hasManageOrdersPermission]);

	return (
		<div>
			<Breadcrumbs items={BREADCRUMBS} />
			<div className="table-container table-container--tabbed">
				<div>
					<TabNavigation
						active={activeTabId}
						onClick={selectTab}
						tabs={tabs}
					/>
					{renderTab()}
				</div>
			</div>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { userData, companyData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasManageOrdersPermission = isAllowed(
		PagePermissions.COMPANY.ORDER.MANAGE,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		companyName: companyData.name,
		hasManageOrdersPermission,
	};
}

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentClass>(
	connector,
	SettingsUtils.withSettings<SettingProps>(() => ([
		{
			key: SettingsKeys.ORDERS_TAB(),
			mappedName: 'tabId',
			normalize: (item: string) => +item,
			defaultValue: TAB_IDS.ORDERS,
			source: BrowserStorageEnum.LOCAL_STORAGE,
		},
	]))
);

export default enhance(OrdersPage);
